import { Dialog, DialogContent, DialogTitle } from '@heidi/design-system/dialog';
import { Skeleton } from '@heidi/design-system/skeleton';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useAtom } from 'jotai';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { getHeidi2Ports, heidi2RequestAccessModalOpenAtom } from '../../../data/heidi-2';

import { heidi2RequestAccessModalMessages } from './heidi-2-request-access-modal.messages';

const Heidi2RequestAccessModalLoading = () => (
  <div className="flex h-[580px] flex-col gap-6 p-8" aria-hidden="true">
    <Skeleton className="h-52 w-full rounded-xl" />
    <Skeleton className="h-16 w-full rounded-lg" />
    <Skeleton className="h-24 w-full rounded-lg" />
  </div>
);

// `React.lazy` rather than `next/dynamic`: this zone may not reach for Next
// beyond `next/image`, and the dialog only renders client-side anyway.
const Heidi2RequestAccessModalContent = lazy(() =>
  import('./heidi-2-request-access-modal-content').then((module) => ({
    default: module.Heidi2RequestAccessModalContent,
  }))
);

export function Heidi2RequestAccessModal() {
  const intl = useIntl();
  const ports = getHeidi2Ports();
  const [isOpen, setIsOpen] = useAtom(heidi2RequestAccessModalOpenAtom);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const viewTrackedRef = useRef(false);

  const trackButtonClicked = ports.trackButtonClicked;
  const trackModalViewed = ports.trackModalViewed;

  const trackDismiss = useCallback(() => {
    trackButtonClicked({
      screen_name: ScreenName.Heidi2RequestAccessModal,
      action: ButtonAction.Heidi2RequestAccessModalDismiss,
      label: 'dismiss',
    });
  }, [trackButtonClicked]);

  useEffect(() => {
    if (!isDialogVisible) {
      viewTrackedRef.current = false;
      return;
    }
    if (viewTrackedRef.current) return;

    viewTrackedRef.current = true;
    trackModalViewed({ screenName: ScreenName.Heidi2RequestAccessModal });
  }, [isDialogVisible, trackModalViewed]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setIsOpen}
      onOpenChangeComplete={setIsDialogVisible}
    >
      <DialogContent
        aria-describedby={undefined}
        className="scrollbar-subtle max-h-[90dvh] max-w-[500px] gap-0 overflow-y-auto p-0"
        closeIconClassName="text-text-primary-on-light"
        onCloseIconClick={trackDismiss}
        onEscapeKeyDown={trackDismiss}
        onPointerDownOutside={trackDismiss}
      >
        <DialogTitle className="sr-only">
          {intl.formatMessage(heidi2RequestAccessModalMessages.title)}
        </DialogTitle>
        {isDialogVisible ? (
          <Suspense fallback={<Heidi2RequestAccessModalLoading />}>
            <Heidi2RequestAccessModalContent />
          </Suspense>
        ) : (
          <Heidi2RequestAccessModalLoading />
        )}
      </DialogContent>
    </Dialog>
  );
}
